import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, MessageCircle, Check, Plus, Minus, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Business facts (one source of truth for the public site) ───────────── */
export const LOGO_URL = "https://customer-assets.emergentagent.com/job_distill-text/artifacts/6pp3o0v1_PX_Logo.png";
export const PHONE = "919845019915";
export const PHONE_DISPLAY = "+91 98450 19915";
export const EMAIL = "info@printxpress.shop";
export const HOURS = "Mon–Sat, 9 am – 8 pm";
export const waLink = (text) => `https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`;

export const BRANCHES = [
  {
    name: "Koramangala 7th Block",
    address: "405, 1st Main Rd, 7th Block, Koramangala, Bengaluru 560095",
    note: "Head office. Digital, offset and flex printing.",
    map: "https://maps.google.com/?q=405,+1st+Main+Rd,+7th+Block,+Koramangala,+Bengaluru,+Karnataka+560095",
  },
  {
    name: "Koramangala 5th Block",
    address: "Near Jyothi Nivas College & Black Pearl Restaurant, Koramangala",
    note: "Digital and flex printing.",
    map: "https://maps.google.com/?q=Print+Xpress+Koramangala+5th+Block+Bengaluru",
  },
  {
    name: "Richmond Road",
    address: "Opposite Elenza Hotel, Shanthala Nagar, Bengaluru",
    note: "Central Bengaluru branch.",
    map: "https://maps.google.com/?q=Print+Xpress+Richmond+Road+Bengaluru",
  },
];

export const SERVICES = [
  { name: "Visiting cards", slug: "visiting-card-printing-bangalore" },
  { name: "T-shirts", slug: "t-shirt-printing-bangalore" },
  { name: "Mugs", slug: "mug-printing-bangalore" },
  { name: "Flyers", slug: "flyer-printing-bangalore" },
  { name: "Corporate gifting", slug: "corporate-gifting-bangalore" },
  { name: "ID cards", slug: "id-card-printing-bangalore" },
  { name: "Sign boards", slug: "sign-board-printing-bangalore" },
  { name: "Certificates", slug: "certificate-printing-bangalore" },
  { name: "Promotional products", slug: "promotional-product-printing-bangalore" },
  { name: "Posters", slug: "poster-printing-bangalore" },
  { name: "Standees", slug: "standee-printing-bangalore" },
  { name: "Screen printing", slug: "screen-printing-bangalore" },
  { name: "Office stationery", slug: "office-stationery-printing-bangalore" },
  { name: "Print on demand", slug: "print-on-demand-bangalore" },
  { name: "Wedding cards", slug: "wedding-cards-printing-bangalore" },
  { name: "Flex banners", slug: "flex-banner-printing-bangalore" },
  { name: "Diaries", slug: "diary-printing-bangalore" },
  { name: "Brochures", slug: "brochure-printing-bangalore" },
  { name: "Calendars", slug: "calendar-printing-bangalore" },
];

export const SERVICE_OPTIONS = [
  "Visiting Card Printing", "T Shirt Printing", "Mug Printing", "Flyer Printing",
  "Corporate Gifting", "ID Card Printing", "Sign Board Printing", "Certificate Printing",
  "Poster Printing", "Standee Printing", "Brochure Printing", "Calendar Printing",
  "Wedding Cards Printing", "Flex Banner Printing", "Other",
];

/* The four process inks, used to key repeating items (bullets, swatches). */
export const INKS = ["#00A3E0", "#E5007D", "#FFED00", "#161616"];

/* ── Layout ─────────────────────────────────────────────────────────────── */
export function Container({ className = "", children, ...rest }) {
  return (
    <div className={`mx-auto w-full max-w-page px-4 sm:px-6 lg:px-10 ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* Registration target — the crosshair-in-circle printed outside the trim. */
export function RegMark({ className = "", size = 22 }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
      <circle cx="12" cy="12" r="6.5" />
      <path d="M12 0v24M0 12h24" />
    </svg>
  );
}

/* Printer's colour control strip: solids, tints and two-colour overprints. */
const BAR = [
  "#00A3E0", "#E5007D", "#FFED00", "#161616",
  "#66C8EC", "#EF66B1", "#FFF466", "#8A8A8A",
  "#2E3192", "#ED1C24", "#00A651", "#161616",
  "#B3E3F6", "#F7B3D8", "#FFF9B3", "#D0D0D0",
];
export function ColorBar({ className = "", patches = 16, onInk = false }) {
  return (
    <div className={`flex h-3 w-full ${className}`} aria-hidden="true">
      {Array.from({ length: patches }).map((_, i) => {
        const c = BAR[i % BAR.length];
        return <span key={i} className="flex-1" style={{ backgroundColor: onInk && c === "#161616" ? "#F4F3EF" : c }} />;
      })}
    </div>
  );
}

/* Section heading, left-aligned, with an optional action on the right. */
export function SectionHead({ title, lede, action, id, dark = false, className = "" }) {
  return (
    <div className={`mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6 ${className}`}>
      <div className="max-w-2xl">
        <h2 id={id} className={`font-display font-bold text-[2.25rem] sm:text-5xl ${dark ? "text-paper" : "text-ink"}`}>
          {title}
        </h2>
        {lede && <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-paper/70" : "text-graphite"}`}>{lede}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/* Header for inner pages. Crumbs → title → lede, colour bar along the bottom. */
export function PageHeader({ crumbs = [], title, lede, children, aside }) {
  return (
    <header className="relative bg-paper pt-28 md:pt-36 border-b border-ink">
      <Container className="relative pb-12 md:pb-16">
        <RegMark className="hidden md:block absolute right-6 lg:right-10 -top-6 text-ink/40" />
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-graphite">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {crumbs.map((c, i) => (
                <li key={i} className="flex items-center gap-2">
                  {i > 0 && <span aria-hidden="true">/</span>}
                  {c.to ? <Link to={c.to} className="hover:text-ink underline-offset-4 hover:underline">{c.label}</Link>
                        : <span className="text-ink">{c.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <div className={`grid gap-10 ${aside ? "lg:grid-cols-[1fr_minmax(0,26rem)] lg:items-end" : ""}`}>
          <div className="max-w-3xl">
            <h1 className="font-display font-bold text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-7xl tracking-[-0.035em] text-ink">
              {title}
            </h1>
            {lede && <p className="mt-6 text-lg sm:text-xl leading-relaxed text-graphite max-w-2xl">{lede}</p>}
            {children && <div className="mt-8">{children}</div>}
          </div>
          {aside}
        </div>
      </Container>
      <ColorBar />
    </header>
  );
}

/* Call + WhatsApp pair. */
export function ContactButtons({ message = "Hi, I'd like a quote for a print job.", className = "", stretch = false, dark = false }) {
  const w = stretch ? "flex-1" : "";
  const callCls = dark ? "btn btn-line !text-paper !border-paper hover:!bg-paper hover:!text-ink" : "btn btn-ink";
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      <a href={`tel:+${PHONE}`} className={`${callCls} ${w}`} data-testid="call-btn">
        <Phone size={16} aria-hidden="true" /> Call {PHONE_DISPLAY}
      </a>
      <a href={waLink(message)} target="_blank" rel="noreferrer" className={`btn btn-whatsapp ${w}`} data-testid="whatsapp-btn">
        <MessageCircle size={16} aria-hidden="true" /> WhatsApp us
      </a>
    </div>
  );
}

/* Image with a halftone placeholder when there's no picture yet. */
export function Picture({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`flex items-center justify-center bg-[#ECEAE3] text-[#CFCCC3] halftone ${className}`} role="img" aria-label={alt}>
        <ImageOff size={28} className="text-graphite/50" aria-hidden="true" />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
}

/* Accordion list of questions. */
export function FaqList({ faqs, testid = "faq" }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-ink">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border-b border-ink" data-testid={`${testid}-item-${i}`}>
            <h3>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-start justify-between gap-6 py-5 text-left font-display font-medium text-lg sm:text-xl text-ink hover:text-process-magenta transition-colors"
              >
                <span>{f.q}</span>
                {isOpen ? <Minus size={20} className="mt-1 shrink-0" aria-hidden="true" /> : <Plus size={20} className="mt-1 shrink-0" aria-hidden="true" />}
              </button>
            </h3>
            {isOpen && <p className="pb-6 pr-10 text-graphite leading-relaxed max-w-2xl">{f.a}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Quote form ─────────────────────────────────────────────────────────
   Every enquiry on the site goes through here and posts to /api/quote. */
export function QuoteForm({
  title = "Get a quote",
  intro,
  service = "",
  lockService = false,
  messageLabel = "What do you need printed?",
  messagePlaceholder = "Quantity, size, paper or material, and when you need it",
  messagePrefix = "",
  submitLabel = "Send quote request",
  testid = "quote-form",
  className = "",
}) {
  const empty = { name: "", email: "", phone: "", service, message: "" };
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        service: form.service || service || undefined,
        message: messagePrefix ? `${messagePrefix}\n\n${form.message}` : form.message,
      });
      setForm(empty);
      setState("sent");
    } catch {
      setState("error");
    }
  };

  if (state === "sent") {
    return (
      <div className={`bg-stock border border-ink p-8 sm:p-10 ${className}`} data-testid={`${testid}-success`} role="status">
        <div className="w-12 h-12 rounded-full bg-process-yellow flex items-center justify-center mb-6">
          <Check size={24} aria-hidden="true" />
        </div>
        <h3 className="font-display font-bold text-2xl sm:text-3xl text-ink">Request received</h3>
        <p className="mt-3 text-graphite leading-relaxed max-w-sm">
          We'll reply with a price within one working day. Need it sooner? Call {PHONE_DISPLAY}.
        </p>
        <button type="button" onClick={() => setState("idle")} className="btn btn-line mt-8">Send another request</button>
      </div>
    );
  }

  const uid = testid;
  return (
    <form onSubmit={submit} data-testid={testid} className={`bg-stock border border-ink p-6 sm:p-8 ${className}`}>
      {title && <h3 className="font-display font-bold text-2xl text-ink">{title}</h3>}
      {intro && <p className="mt-2 text-graphite">{intro}</p>}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5 ${title || intro ? "mt-6" : ""}`}>
        <div>
          <label htmlFor={`${uid}-name`} className="field-label">Name</label>
          <input id={`${uid}-name`} className="field" required autoComplete="name" value={form.name} onChange={set("name")} data-testid={`${uid}-name`} />
        </div>
        <div>
          <label htmlFor={`${uid}-phone`} className="field-label">Phone <span className="text-graphite font-normal">(optional)</span></label>
          <input id={`${uid}-phone`} className="field" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid={`${uid}-phone`} />
        </div>
        <div className={lockService ? "sm:col-span-2" : ""}>
          <label htmlFor={`${uid}-email`} className="field-label">Email</label>
          <input id={`${uid}-email`} className="field" type="email" required autoComplete="email" value={form.email} onChange={set("email")} data-testid={`${uid}-email`} />
        </div>
        {!lockService && (
          <div>
            <label htmlFor={`${uid}-service`} className="field-label">Service</label>
            <select id={`${uid}-service`} className="field" value={form.service} onChange={set("service")} data-testid={`${uid}-service`}>
              <option value="">Not sure yet</option>
              {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="sm:col-span-2">
          <label htmlFor={`${uid}-message`} className="field-label">{messageLabel}</label>
          <textarea id={`${uid}-message`} className="field resize-y min-h-[7rem]" required rows={4}
            placeholder={messagePlaceholder} value={form.message} onChange={set("message")} data-testid={`${uid}-message`} />
        </div>
      </div>
      {state === "error" && (
        <p className="mt-5 text-sm text-[#B00020] border-l-2 border-[#B00020] pl-3" role="alert">
          The request didn't go through. Check your connection and try again, or call {PHONE_DISPLAY}.
        </p>
      )}
      <button type="submit" disabled={state === "sending"} className="btn btn-magenta w-full mt-6" data-testid={`${uid}-submit`}>
        {state === "sending" ? "Sending…" : submitLabel}
      </button>
      <p className="mt-4 text-sm text-graphite">We reply within one working day, usually much sooner.</p>
    </form>
  );
}
